// src/components/PrivateRoute.jsx
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, authReady, db } from '../firebase';

export default function PrivateRoute({ children, allow }) {
  const [user, loading] = useAuthState(auth);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    let isMounted = true;

    authReady.finally(() => {
      if (isMounted) {
        setAuthInitialized(true);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function checkAccess() {
      if (!user) {
        if (isMounted) {
          setHasAccess(false);
          setCheckingAccess(false);
        }
        return;
      }

      if (!allow) {
        if (isMounted) {
          setHasAccess(true);
          setCheckingAccess(false);
        }
        return;
      }

      const allowedRoles = Array.isArray(allow) ? allow : [allow];
      const snap = await getDoc(doc(db, 'users', user.uid));
      const profile = snap.exists() ? snap.data() : null;

      const profileRole = String(profile?.role || '').toLowerCase();
      const normalizedAllowedRoles = allowedRoles.map((role) => String(role).toLowerCase());
      const roleAllowed = profile && normalizedAllowedRoles.includes(profileRole);
      const approvalAllowed =
        profileRole !== 'admin' || profile?.approved === true;
      const paymentAllowed =
        profileRole !== 'agent' || profile?.paid !== false;

      if (isMounted) {
        setHasAccess(Boolean(roleAllowed && approvalAllowed && paymentAllowed));
        setCheckingAccess(false);
      }
    }

    setCheckingAccess(true);
    checkAccess();

    return () => {
      isMounted = false;
    };
  }, [allow, user]);

  if (!authInitialized || loading || checkingAccess) {
    return <div className="text-center p-8 text-white">Checking authentication...</div>;
  }

  const allowedRoles = Array.isArray(allow) ? allow : [allow];
  const redirectPath = allowedRoles.includes('admin') ? '/admin' : '/agent/login';

  return user && hasAccess ? children : <Navigate to={redirectPath} replace />;
}